import type { DataChartStyleProps } from './datachartStyleRegistry'
import { useEnergyDeviceSnapshot } from './useEnergyDeviceSnapshot'
import './EnergyCard.css'

function formatInt(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US')
}

function formatFixed(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '--'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

function normalizeUnitToken(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

function formatEnergyByUnit(value: number | null, rawUnit: string | null): { value: number | null; unit: string } {
  if (value === null || !Number.isFinite(value)) {
    return { value: null, unit: 'kWh' }
  }
  const originalUnit = String(rawUnit || '').trim()
  const token = normalizeUnitToken(originalUnit)

  // Canonical display unit: kWh
  if (token === 'wh' || token === 'watthour' || token === 'watthours') {
    return { value: value / 1000, unit: 'kWh' }
  }
  if (token === 'kwh' || token === 'kilowatthour' || token === 'kilowatthours') {
    return { value, unit: 'kWh' }
  }
  if (token === 'mwh' || token === 'megawatthour' || token === 'megawatthours') {
    return { value: value * 1000, unit: 'kWh' }
  }

  return { value, unit: originalUnit || 'kWh' }
}

export function EnergyCardStyle04(props: DataChartStyleProps) {
  const { snapshot } = useEnergyDeviceSnapshot(props.deviceSN, props.credentials)
  const power = snapshot?.power ?? null
  const voltage = snapshot?.voltage ?? null
  const current = snapshot?.current ?? null
  const powerFactor = snapshot?.powerFactor ?? null
  const frequency = snapshot?.frequency ?? null
  const energy = snapshot?.energy ?? null
  const energyDisplay = formatEnergyByUnit(energy, snapshot?.energyUnit ?? null)

  return (
    <div className="energy-card energy-card--single" data-css-slot="cardFrame" data-css-slot-label="Card Frame">
      <div className="energy-card-single__head">
        <div>
          <div className="energy-card-single__title">Single-Phase Power</div>
          <div className="energy-card-single__subtitle">L-N Monitoring</div>
        </div>
        <div className="energy-card-single__live">
          <span className="energy-card-single__live-dot" />
          LIVE
        </div>
      </div>

      <div className="energy-card-single__main-value">
        {formatInt(power)}
        <small>W</small>
      </div>

      <div className="energy-card-single__metrics">
        <div className="energy-card-single__metric">
          <span>Voltage</span>
          <strong>{formatFixed(voltage, 1)} V</strong>
        </div>
        <div className="energy-card-single__metric">
          <span>Current</span>
          <strong>{formatFixed(current, 1)} A</strong>
        </div>
        <div className="energy-card-single__metric">
          <span>Power Factor</span>
          <strong>{formatFixed(powerFactor, 2)}</strong>
        </div>
        <div className="energy-card-single__metric">
          <span>Frequency</span>
          <strong>{formatFixed(frequency, 2)} Hz</strong>
        </div>
      </div>

      <div className="energy-card-single__footer">
        <span>Energy</span>
        <strong>{formatFixed(energyDisplay.value, 1)} {energyDisplay.unit}</strong>
      </div>
    </div>
  )
}
